import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';

import { Alert, Button, Typography } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';

import { formatDate, getDisplayCompanyId } from 'utils/helpers';
import { ORGANIZATION_NAME } from 'utils/objects';

import TableResult from 'components/Common/TableResult';

import SearchMemberResultsTableProps from './Proptypes';

import packageJson from '../../../../package.json';

import styles from './SearchMemberResultsTable.module.scss';

const { Title } = Typography;

const SearchMemberResultsTable = (props: SearchMemberResultsTableProps) => {
  const searchMemberColumns = [
    {
      title: 'IPA',
      key: 'RmiCompanyId',
      dataIndex: 'RmiCompanyId',
      render: (text: string) => getDisplayCompanyId(text)
    },
    {
      title: 'MEMBER ID',
      key: 'RmiMembId',
      dataIndex: 'RmiMembId',
      render: (_: number, record: any) => {
        return (
          <Link to={{
            pathname: '/eligibility/member-detail',
            state: {
              eligibilityMemberInfo: record
            }
          }}>
            {record.RmiMembId}
          </Link>
        );
      },
      sorter: (a: any, b: any) => a.RmiMembId.localeCompare(b.RmiMembId)
    },
    {
      title: 'FULL NAME',
      key: 'RmiLastName, RmiFirstName',
      dataIndex: 'RmiLastName, RmiFirstName',
      render: (_: number, record: any) => record.RmiLastName + ', ' + record.RmiFirstName,
      sorter: (a: any, b: any) => (a.RmiLastName + ', ' + a.RmiFirstName).localeCompare((b.RmiLastName + ', ' + b.RmiFirstName))
    },
    {
      title: 'GENDER',
      key: 'RmiSex',
      dataIndex: 'RmiSex',
    },
    {
      title: 'DATE OF BIRTH',
      key: 'RmiBirth',
      dataIndex: 'RmiBirth',
      render: (_: number, record: any) => formatDate(record.RmiBirth, 'MM/DD/YYYY'),
      sorter: (a: any, b: any) => a.RmiBirth.localeCompare(b.RmiBirth)
    },
    {
      title: 'LANGUAGE',
      key: 'RmiLanguage',
      dataIndex: 'RmiLanguage',
      sorter: (a: any, b: any) => {
        if (a && a.RmiLanguage && a.RmiLanguage.length && b && b.RmiLanguage && b.RmiLanguage.length) {
          return a.RmiLanguage.localeCompare(b.RmiLanguage);
        }
        return 0;
      }
    },
    {
      title: 'HEALTH PLAN',
      key: 'RmiHpName',
      dataIndex: 'RmiHpName',
    },
    {
      title: 'OPTION',
      key: 'RmiOpt',
      dataIndex: 'RmiOpt',
    },
    {
      title: 'START DATE',
      key: 'RmiOpFromDt',
      dataIndex: 'RmiOpFromDt',
      render: (_: number, record: any) => formatDate(record.RmiOpFromDt, 'MM/DD/YYYY'),
    },
    {
      title: 'TERM DATE',
      key: 'RmiEndDate',
      dataIndex: 'RmiEndDate',
      render: (_: number, record: any) => {
        if ((record.RmiEndDate === null || record.RmiEndDate > moment())) {
          return (<>{formatDate(record.RmiEndDate, 'MM/DD/YYYY')}</>)
        } else {
          return (
            <>
              <span className={styles['inline']}>{formatDate(record.RmiEndDate, 'MM/DD/YYYY')}</span>
              <span className={`${styles['inline']} ${styles['red-asterisks']}`}>*</span>
            </>
          );
        }
      }
    },
    {
      title: 'ELIGIBLE',
      key: 'eligible',
      dataIndex: 'eligible',
      render: (_: number, record: any) => (record.RmiEndDate === null || record.RmiEndDate > moment()) ? 'Yes' : 'No' // TODO: This should be done on the API side
    },
  ];

  const [version, setVersion] = useState('');
  const [currentTime, setCurrentTime] = useState('');

  useEffect(() => {
    getVersion();
  }, []);

  const print = async () => {
    $(".print-logo").removeClass('exclude-print').addClass("include-print");
    await setCurrentTime(moment().format('MMM DD, YYYY hh:mm:ss A'));
    window.print();
  };

  const getVersion = () => {
    setVersion(packageJson.version);
  }

  return (
    <>
      <div className={styles['print-eligibility-search-section']}>
        <Title level={5} className="page-title" style={{float: 'left'}}>Members</Title>
        <Button type="primary" className={`mb-2 ${styles['print-button']}`} style={{ float: 'right' }} onClick={print} icon={<PrinterOutlined />}>Print</Button>
        <TableResult
          className={styles['eligibility-results']}
          columns={searchMemberColumns}
          dataSource={props.dataSource}
          loading={props.isSearchingMembers}
          showSizeChanger={false}
        />
        <Alert
          className="mt-2"
          message=""
          description={
            <>
              ELIGIBILITY PROVIDED BY THE IPA DOES NOT GUARANTEE THE MEMBER&#8217;S UP-TO-DATE ELIGIBILITY INFORMATION.
              PLEASE VERIFY ELIGIBILITY AT THE TIME OF SERVICE WITH THE MEMBER&#8217;S HEALTH PLAN.
              THE IPA IS NOT RESPONSIBLE FOR SERVICES RENDERED FOR MEMBERS NOT ELIGIBLE WITH THE IPA AT THE TIME OF SERVICE.
            </>
          }
        />
        <div>
        <div className="print-timestamp"><b>Copyright</b> {ORGANIZATION_NAME}, Inc. &copy; {moment().year()} All Rights Reserved
          &nbsp; &nbsp; V.{version} &nbsp; &nbsp;</div>
        </div>
      </div>
    </>
  );
}

export default SearchMemberResultsTable;